import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Download, X, ChevronLeft, ChevronRight, Camera, Share2, Copy, Check } from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const cleanName = (s) => {
  if (!s) return "Votre mariage";
  const c = String(s).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(c) ? "Votre mariage" : c;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

export default function MyEventMemories() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [openIdx, setOpenIdx] = useState(-1);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [thumbs, setThumbs] = useState({});

  useEffect(() => {
    if (!eventId) return;
    api.get(`/next/myevent/${eventId}/dashboard`).then(r => setDashboard(r.data)).catch(() => {});
    api.get(`/next/myevent/${eventId}/memories`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.status || 500));
  }, [eventId]);

  const photos = data?.photos || [];
  const fileUrl = (photoId) => `${API}/next/myevent/${eventId}/memories/${photoId}/file`;

  // Fetch thumbnails via blob (needs Authorization header)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = getToken();
      for (const p of photos) {
        if (thumbs[p.id]) continue;
        try {
          const r = await fetch(fileUrl(p.id), { headers: { Authorization: `Bearer ${token}` } });
          if (!r.ok) continue;
          const b = await r.blob();
          if (cancelled) return;
          setThumbs(prev => ({ ...prev, [p.id]: URL.createObjectURL(b) }));
        } catch {}
      }
    };
    if (photos.length) load();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [photos.length]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <ErrorScreen title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="mymem-forbidden" />;
  if (err === 404) return <ErrorScreen title="Événement introuvable" text="Cet événement n'existe pas." testid="mymem-notfound" />;

  const shares = data?.active_shares || [];
  const ev = dashboard?.event || {};
  const couple = cleanName(ev.name);

  const downloadPhoto = async (p) => {
    try {
      const token = getToken();
      const r = await fetch(fileUrl(p.id), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = p.original_filename || `photo-${p.id}.jpg`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible."); }
  };

  const publicGalleryUrl = (code) => `${window.location.origin}/galerie/${code}`;
  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(publicGalleryUrl(code));
      setCopied(true); toast.success("Lien copié.");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Impossible de copier."); }
  };

  const next = () => setOpenIdx(i => (i + 1) % photos.length);
  const prev = () => setOpenIdx(i => (i - 1 + photos.length) % photos.length);

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-memories">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="mymem-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyMemories</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />
        <main className="lg:col-span-9 min-w-0 space-y-8">
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Vos <span className="italic gold-text">souvenirs.</span></h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">Les photos de votre mariage.</p>
            {ev.date && <p className="mt-1 text-[#DCB457] text-sm" data-testid="mymem-event-summary">{couple} — {fmtDate(ev.date)}</p>}
          </section>

          {/* Galerie à partager (uniquement si code actif) */}
          {shares.length > 0 && (
            <section className="card-luxe p-5 md:p-6" data-testid="mymem-share-block">
              <div className="flex items-center gap-3 mb-3">
                <Share2 size={16} className="text-[#DCB457]" strokeWidth={1.4} />
                <p className="eyebrow">Galerie à partager</p>
              </div>
              {shares.map(s => (
                <div key={s.code} className="flex flex-col md:flex-row md:items-center gap-3 py-2" data-testid={`mymem-share-${s.code}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-luxe text-lg">Code : <span className="gold-text tracking-widest" data-testid="mymem-share-code">{s.code}</span></p>
                    <p className="text-[10px] uppercase tracking-widest text-white/45 mt-1">
                      Expire le {new Date(s.expires_at).toLocaleDateString("fr-FR")} · {s.views} vue{s.views > 1 ? "s" : ""} · {s.downloads} téléchargement{s.downloads > 1 ? "s" : ""}
                    </p>
                  </div>
                  <button onClick={() => copyCode(s.code)} data-testid={`mymem-share-copy-${s.code}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-xs uppercase tracking-widest">
                    {copied ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier le lien</>}
                  </button>
                </div>
              ))}
              <p className="text-[10px] text-white/40 mt-3 italic">
                La génération et la révocation des codes de partage sont assurées par My Animation.
              </p>
            </section>
          )}

          {shares.length === 0 && photos.length > 0 && (
            <p className="text-[10px] uppercase tracking-widest text-white/35" data-testid="mymem-no-share">
              Aucune galerie publique disponible pour le moment.
            </p>
          )}

          {/* Grille photos */}
          <section data-testid="mymem-grid">
            {!data ? (
              <p className="text-white/50 italic text-sm">Chargement…</p>
            ) : photos.length === 0 ? (
              <div className="card-luxe p-10 text-center">
                <Camera size={30} className="text-[#DCB457]/40 mx-auto mb-4" strokeWidth={1.2} />
                <p className="font-serif-luxe text-2xl mb-2">Vos souvenirs s'écrivent…</p>
                <p className="text-white/55 text-sm max-w-md mx-auto">
                  Les photos de votre mariage apparaîtront ici dès leur téléchargement par notre équipe.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {photos.map((p, i) => (
                  <button key={p.id} onClick={() => setOpenIdx(i)}
                    data-testid={`mymem-photo-${p.id}`}
                    className="group aspect-square bg-black/60 border border-[#DCB457]/15 hover:border-[#DCB457] overflow-hidden relative">
                    {thumbs[p.id] ? (
                      <img src={thumbs[p.id]} alt={p.original_filename}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/25">
                        <Camera size={22} strokeWidth={1.3} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Lightbox */}
      {openIdx >= 0 && photos[openIdx] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-8" data-testid="mymem-lightbox">
          <button onClick={() => setOpenIdx(-1)} data-testid="mymem-lightbox-close"
            className="absolute top-4 right-4 text-white/70 hover:text-[#DCB457] p-2">
            <X size={22} />
          </button>
          {photos.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#DCB457] p-2" data-testid="mymem-lightbox-prev">
                <ChevronLeft size={26} />
              </button>
              <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#DCB457] p-2" data-testid="mymem-lightbox-next">
                <ChevronRight size={26} />
              </button>
            </>
          )}
          <div className="max-w-5xl max-h-[85vh] flex flex-col items-center">
            {thumbs[photos[openIdx].id] ? (
              <img src={thumbs[photos[openIdx].id]}
                className="max-w-full max-h-[80vh] object-contain border border-[#DCB457]/30" />
            ) : (
              <p className="text-white/50 italic">Chargement…</p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <p className="text-[10px] uppercase tracking-widest text-white/50">
                {openIdx + 1} / {photos.length}
              </p>
              <button onClick={() => downloadPhoto(photos[openIdx])} data-testid="mymem-lightbox-download"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-[10px] uppercase tracking-widest">
                <Download size={11} /> Télécharger
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorScreen({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyMemories</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
